// /app/frontend/src/components/frugal-v2/sections/TransactionsSection.jsx
// Unscheduled expenses section with account selection and receipt scanner (< 200 lines)

import React, { useState } from 'react';
import { CreditCard, Mail } from 'lucide-react';
import CollapsibleSection from '../components/CollapsibleSection';
import AddItemForm from '../components/AddItemForm';
import EditItemModal from '../components/EditItemModal';
import AccountSelector from '../components/AccountSelector';
import { CATEGORY_COLORS } from '../constants/colors';
import { EXPENSE_CATEGORIES, VITAL_CATEGORIES, translateCategory } from '../constants/categories';

const TransactionsSection = ({ 
  transactions = [],
  onAdd,
  onEdit,
  onDelete,
  onViewEmails,
  token,
  fundingStatus,
  availableAmount = 0,
  t 
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [pendingExpense, setPendingExpense] = useState(null);
  
  // Filter only NON-VITAL transactions
  const nonVitalTransactions = transactions.filter(tx => !VITAL_CATEGORIES.includes(tx.category));
  
  const total = nonVitalTransactions.reduce((sum, tx) => sum + (tx.amount || 0), 0);

  const handleAddExpense = async (data) => {
    // Store expense data and show account selector
    setPendingExpense(data);
    setShowForm(false);
  };

  const handleAccountSelect = async (account) => {
    console.log('[TransactionsSection] handleAccountSelect called');
    console.log('[TransactionsSection] pendingExpense:', pendingExpense);
    console.log('[TransactionsSection] account:', account);
    console.log('[TransactionsSection] onAdd exists:', !!onAdd);
    
    if (pendingExpense && onAdd) {
      try {
        // Add expense with account info
        // Map 'name' to 'description' for API compatibility
        const today = new Date().getDate();
        const expenseData = {
          name: pendingExpense.name,
          description: pendingExpense.name,  // Some parts of UI use description
          amount: pendingExpense.amount,
          category: pendingExpense.category || 'Otros',
          day: today,  // Use current day for unscheduled expenses
          paid_with_account: account.id
        };
        console.log('[TransactionsSection] Creating expense:', expenseData);
        await onAdd(expenseData);
        console.log('[TransactionsSection] Expense created successfully');
        setPendingExpense(null);
      } catch (err) {
        console.error('[TransactionsSection] Error creating expense:', err);
      }
    } else {
      console.log('[TransactionsSection] Missing pendingExpense or onAdd');
    }
  };

  const handleSaveEdit = async (id, data) => {
    if (onEdit) await onEdit(id, data);
    setEditingItem(null);
  };

  const addFormComponent = onAdd ? (
    <AddItemForm
      type="expense"
      onAdd={handleAddExpense}
      onCancel={() => setShowForm(false)}
      colorClass="bg-purple-500 hover:bg-purple-600"
      showCategory={true}
      categories={EXPENSE_CATEGORIES}
      t={t}
    />
  ) : null;

  return (
    <>
      <CollapsibleSection
        icon={CreditCard}
        title={t('frugal.transactions.title')}
        total={availableAmount}
        subtitle={`${t('frugal.transactions.spentThisMonth')}: $${Number(total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        iconColorClass="text-purple-400"
        showAddForm={showForm}
        onToggleAddForm={onAdd ? () => setShowForm(!showForm) : undefined}
        addForm={addFormComponent}
        defaultExpanded={false}
        fundingStatus={fundingStatus}
      >
        {/* List - scrolls naturally within the CollapsibleSection wrapper */}
        <div className="space-y-2">
          {nonVitalTransactions.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-4">{t('frugal.transactions.empty')}</p>
          ) : (
            nonVitalTransactions.map(tx => (
              <div
                key={tx.id}
                className="flex items-center justify-between p-3 rounded-lg bg-white/5 group"
              >
                {/* Left: Description + Category */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: CATEGORY_COLORS[tx.category] || CATEGORY_COLORS.Otros }}
                  />
                  <div className="min-w-0 flex-1">
                    <span className="text-sm text-white truncate block">{tx.description || tx.name}</span>
                    <span className="text-xs text-slate-500">{translateCategory(t, tx.category)}</span>
                  </div>
                </div>

                {/* Right: Amount + Edit */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-purple-400">
                    ${tx.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  {onEdit && (
                    <button
                      onClick={() => setEditingItem(tx)}
                      className="text-slate-500 hover:text-white transition-all p-1"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* View Emails Button */}
        {onViewEmails && (
          <button
            onClick={onViewEmails}
            className="mt-3 w-full py-2 flex items-center justify-center gap-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-sm"
          >
            <Mail className="w-4 h-4" />
            {t('frugal.transactions.viewEmails')}
          </button>
        )}
      </CollapsibleSection>

      {/* Edit Modal */}
      {editingItem && (
        <EditItemModal
          item={editingItem}
          type="expense"
          onSave={handleSaveEdit}
          onDelete={onDelete}
          onClose={() => setEditingItem(null)}
          showCategory={true}
          categories={EXPENSE_CATEGORIES}
          t={t}
        />
      )}

      {/* Account Selector Modal */}
      <AccountSelector
        isOpen={!!pendingExpense}
        onClose={() => setPendingExpense(null)}
        onSelect={handleAccountSelect}
        amount={pendingExpense?.amount}
        description={pendingExpense?.name}
        token={token}
        t={t}
      />
    </>
  );
};

export default TransactionsSection;
